import { useEffect, useState } from 'react';

interface PainPointsProps {
  title: string;
  subtitle: string;
  problems: string[];
  solution: string;
  isVisible: boolean;
}

export default function PainPoints({
  title,
  subtitle,
  problems,
  solution,
  isVisible,
}: PainPointsProps) {
  const [visibleBubbles, setVisibleBubbles] = useState<number[]>([]);

  useEffect(() => {
    if (isVisible) {
      // Stagger the bubble animations
      problems.forEach((_, index) => {
        setTimeout(() => {
          setVisibleBubbles((prev) => [...prev, index]);
        }, index * 200);
      });
    } else {
      setVisibleBubbles([]);
    }
  }, [isVisible, problems]);

  // Position bubbles in a scattered pattern
  const bubblePositions = [
    { top: '5%', left: '55%', delay: 0 },
    { top: '15%', left: '70%', delay: 1 },
    { top: '30%', left: '50%', delay: 2 },
    { top: '45%', left: '65%', delay: 3 },
    { top: '55%', left: '45%', delay: 4 },
    { top: '70%', left: '60%', delay: 5 },
  ];

  return (
    <section className="py-24 md:py-32 px-6 relative overflow-hidden bg-gradient-to-b from-[var(--navy-deep)] via-[var(--navy-mid)] to-[var(--navy-deep)]">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--cyan-electric)] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--amber-warm)] rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side - Solution message */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="gradient-text">{title}</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-8">
              {subtitle}
            </p>

            {/* Solution badge */}
            <div
              className={`inline-block transition-all duration-700 delay-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="px-6 py-3 rounded-full bg-gradient-to-r from-[var(--cyan-electric)]/20 to-[var(--amber-warm)]/20 border border-[var(--cyan-electric)]/30">
                <span className="text-[var(--cyan-electric)] font-semibold">{solution}</span>
              </div>
            </div>
          </div>

          {/* Right side - Speech bubbles */}
          <div className="relative h-[500px] md:h-[600px]">
            {problems.map((problem, index) => {
              const position = bubblePositions[index % bubblePositions.length];
              const isOdd = index % 2 === 1;

              return (
                <div
                  key={index}
                  className={`absolute transition-all duration-700 ease-out ${
                    visibleBubbles.includes(index)
                      ? 'opacity-100 scale-100'
                      : 'opacity-0 scale-75'
                  }`}
                  style={{
                    top: position.top,
                    left: isOdd ? undefined : position.left,
                    right: isOdd ? `calc(100% - ${position.left} - 20%)` : undefined,
                    transitionDelay: `${index * 150}ms`,
                    maxWidth: '280px',
                  }}
                >
                  {/* Speech bubble */}
                  <div
                    className={`relative p-4 rounded-2xl backdrop-blur-sm ${
                      isOdd
                        ? 'bg-white/10 border border-white/20'
                        : 'bg-[var(--cyan-electric)]/10 border border-[var(--cyan-electric)]/20'
                    }`}
                  >
                    <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                      {problem}
                    </p>

                    {/* Bubble tail */}
                    <div
                      className={`absolute w-4 h-4 ${
                        isOdd
                          ? 'bg-white/10 border-l border-b border-white/20 -left-2 top-4 rotate-45'
                          : 'bg-[var(--cyan-electric)]/10 border-r border-b border-[var(--cyan-electric)]/20 -right-2 top-4 -rotate-45'
                      }`}
                    />
                  </div>
                </div>
              );
            })}

            {/* Decorative elements */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 opacity-20">
              <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="url(#gradient-painpoints)"
                  strokeWidth="2"
                  strokeDasharray="10 5"
                />
                <defs>
                  <linearGradient id="gradient-painpoints" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--cyan-electric)" />
                    <stop offset="100%" stopColor="var(--amber-warm)" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
